/*
 * Reads the serial number and warranty out of the iOS "Settings > General >
 * About" screenshot the customer uploads on the IMEI Submission page, fills
 * those two fields, and keeps them hidden so neither can be typed by hand.
 * The form cannot be submitted until both have been read from the screenshot.
 *
 * Replaces the earlier *#06# / IMEI reader: that screen is no longer used.
 *
 * The form is the Shopify Forms app block, which renders into a shadow root at
 * [data-form-root] #app-embed - the same place 'b2b-form-style' reaches into.
 * Two things about that app drive the design, both established against the
 * live form rather than assumed:
 *
 *  - Its inputs are controlled. Assigning `input.value` is undone on the next
 *    render and the field then validates as empty; setting the value through
 *    the native setter and dispatching 'input' updates the app's own state.
 *  - Clearing the file fires a click on its clear button and no 'change' at
 *    all, so removal has to be caught from that click or the values read from
 *    a removed screenshot would still be submitted.
 *
 * WHY THE LEGACY OCR ENGINE. An IMEI carries a Luhn check digit, so a misread
 * was self-detecting. A serial number carries no checksum: whatever is read is
 * submitted. Measured on a real About screenshot, Tesseract's default LSTM
 * engine got the serial wrong in 18 of 18 configurations - consistently 9 -> O
 * and 6 -> G - even cropped, 8x upscaled and binarised. The legacy engine
 * (OEM 0) read it correctly in 6 of 6. It is a classical shape matcher, which
 * suits crisp rendered screen fonts, where the LSTM is tuned for scanned text.
 * The cost is a much larger model download, which is why nothing loads until
 * the customer actually reaches for a file.
 */
(function () {
  'use strict';

  var CFG = window.ABOUT_OCR_CONFIG || {};

  var SERIAL_FIELD = 'custom#serial_number';
  var WARRANTY_FIELD = 'custom#warranty';
  var FILE_FIELD = 'custom#about_screenshot-field';
  var CLEAR_BUTTON = FILE_FIELD.replace(/-field$/, '-clear');

  var TESSERACT_SRC = CFG.tesseractSrc || 'https://cdn.jsdelivr.net/npm/tesseract.js@6.0.1/dist/tesseract.min.js';
  var MAX_BYTES = 12 * 1024 * 1024;

  // Two help images, shown only on demand behind links beside the upload
  // control. URLs come from Liquid so the files stay managed in Shopify Files.
  var IMAGES = CFG.images || {};

  // Phrase inside the consent checkbox label to turn into a link.
  var TERMS = CFG.terms || {};

  var T = CFG.text || {};
  var TEXT = {
    linkHowTo: T.linkHowTo || 'How to take the screenshot',
    linkExample: T.linkExample || 'Screenshot Example',
    close: T.close || 'Close',
    hint: T.hint || '',
    reading: T.reading || 'Reading your screenshot...',
    found: T.found || 'Detected - Serial Number: {serial} | Warranty: {warranty}',
    notFound: T.notFound || 'We could not read your device details from this image. Open Settings > General > About, then upload a clear screenshot that is not cropped.',
    partial: T.partial || 'Only part of your device details could be read ({missing} not found). Make sure that row is fully visible in your screenshot, then upload it again.',
    tooBig: T.tooBig || 'That image is too large. Please upload a screenshot under 12 MB.',
    failed: T.failed || 'Automatic reading failed. Please upload your screenshot again.',
    blocked: T.blocked || 'Your device details have not been read yet. Upload a clear About screenshot before submitting.',
    stillReading: T.stillReading || 'Please wait, we are still reading your screenshot...',
    labelSerial: T.labelSerial || 'serial number',
    labelWarranty: T.labelWarranty || 'warranty'
  };

  /* ---------------------------------------------------------------- parsing */

  // Both label sets, because the store's customers run their phones in either
  // language. Only the English set has been verified against a real capture.
  var RE_SERIAL = /(?:serial\s*(?:number|no\.?)?|nomor\s*seri|no\.?\s*seri)/i;
  var RE_WARRANTY = /(?:limited\s+warranty|applecare\+?|coverage|warranty|garansi\s*terbatas|garansi|jaminan)/i;

  function textLines(text) {
    return String(text).split('\n')
      .map(function (l) { return l.replace(/\s+/g, ' ').trim(); })
      .filter(function (l) { return l.length > 0; });
  }

  // Rows read as "Serial Number   MQVF9HL6KD": the value is whatever trails the
  // label on that line. On a narrow capture it can wrap, so the next line is
  // taken as a fallback.
  function valueAfterLabel(lines, labelRe) {
    for (var i = 0; i < lines.length; i++) {
      var m = lines[i].match(labelRe);
      if (!m) continue;
      var rest = lines[i].slice(m.index + m[0].length).replace(/^[\s:>|]+/, '').trim();
      if (rest) return rest;
      if (lines[i + 1]) return lines[i + 1].trim();
    }
    return null;
  }

  // Trailing chevrons and stray bars are the row's disclosure arrow picked up
  // as text.
  function tidy(v) {
    return v.replace(/[\s>|)\]]+$/, '').replace(/^[\s<|(\[]+/, '').trim();
  }

  // Apple serials are 10 (current) or 11-12 (older) characters, always mixing
  // letters and digits, which rules out words carried over from other rows.
  function looksLikeSerial(s) {
    return !!s && s.length >= 10 && s.length <= 12 &&
      /^[A-Z0-9]+$/.test(s) && /[0-9]/.test(s) && /[A-Z]/.test(s);
  }

  function findSerial(text) {
    var v = valueAfterLabel(textLines(text), RE_SERIAL);
    if (!v) return null;
    var toks = tidy(v).split(/\s+/);
    for (var i = 0; i < toks.length; i++) {
      var t = toks[i].toUpperCase().replace(/[^A-Z0-9]/g, '');
      if (looksLikeSerial(t)) return t;
    }
    return null;
  }

  // Stored exactly as it reads on the phone ("Expires 10/16/26", "Expired"),
  // because the date order depends on the device's locale and is not safe to
  // normalise here.
  function findWarranty(text) {
    var v = valueAfterLabel(textLines(text), RE_WARRANTY);
    if (!v) return null;
    v = tidy(v);
    if (!v || v.length > 60) return null;
    // Must carry either a date or an explicit status word, or it is some other
    // row that happened to match.
    if (!/\d/.test(v) && !/expired|expires|kedaluwarsa|berakhir|aktif|active/i.test(v)) return null;
    return v;
  }

  /* ------------------------------------------------------------ image prep */

  // Grayscale, inverted when the capture is dark mode, and upscaled so the
  // value text clears the recogniser's comfort zone. Deliberately no
  // binarisation: the value column is grey where the labels are near-black, and
  // a threshold that keeps the labels tends to thin the values away. The legacy
  // engine read plain grayscale correctly at every scale tried.
  function preprocess(img) {
    var scale = Math.min(3, Math.max(1, 1800 / img.width));
    // Keep the canvas within a sane size for older phones.
    var maxPixels = 12e6;
    if (img.width * img.height * scale * scale > maxPixels) {
      scale = Math.sqrt(maxPixels / (img.width * img.height));
    }
    var c = document.createElement('canvas');
    c.width = Math.max(1, Math.round(img.width * scale));
    c.height = Math.max(1, Math.round(img.height * scale));
    var ctx = c.getContext('2d', { willReadFrequently: true });
    ctx.imageSmoothingQuality = 'high';
    ctx.drawImage(img, 0, 0, c.width, c.height);

    var d = ctx.getImageData(0, 0, c.width, c.height), p = d.data, sum = 0, i;
    for (i = 0; i < p.length; i += 4) {
      var g = (p[i] * 0.299 + p[i + 1] * 0.587 + p[i + 2] * 0.114) | 0;
      p[i] = p[i + 1] = p[i + 2] = g;
      sum += g;
    }
    if (sum / (p.length / 4) < 128) {
      for (i = 0; i < p.length; i += 4) p[i] = p[i + 1] = p[i + 2] = 255 - p[i];
    }
    ctx.putImageData(d, 0, 0);
    return c;
  }

  /* ---------------------------------------------------------------- engine */

  var scriptPromise = null, workerPromise = null;

  function loadTesseract() {
    if (scriptPromise) return scriptPromise;
    scriptPromise = new Promise(function (resolve, reject) {
      if (window.Tesseract) return resolve(window.Tesseract);
      var s = document.createElement('script');
      s.src = TESSERACT_SRC;
      s.async = true;
      s.onload = function () {
        window.Tesseract ? resolve(window.Tesseract) : reject(new Error('tesseract missing'));
      };
      s.onerror = function () { reject(new Error('tesseract failed to load')); };
      document.head.appendChild(s);
    });
    return scriptPromise;
  }

  // OEM 0 is the legacy engine, which needs its own wasm core and the full
  // language model. Several MB, so it is fetched once and only once the
  // customer shows intent to upload.
  function getWorker() {
    if (workerPromise) return workerPromise;
    workerPromise = loadTesseract().then(function (Tesseract) {
      return Tesseract.createWorker('eng', 0, { legacyCore: true, legacyLang: true });
    });
    return workerPromise;
  }

  function readAbout(file) {
    var url = URL.createObjectURL(file);
    return new Promise(function (resolve, reject) {
      var img = new Image();
      img.onload = function () { resolve(img); };
      img.onerror = function () { reject(new Error('not an image')); };
      img.src = url;
    }).then(function (img) {
      var canvas = preprocess(img);
      return getWorker().then(function (worker) { return worker.recognize(canvas); });
    }).then(function (res) {
      var text = res.data.text;
      return { serial: findSerial(text), warranty: findWarranty(text) };
    }).then(
      function (v) { URL.revokeObjectURL(url); return v; },
      function (e) { URL.revokeObjectURL(url); throw e; }
    );
  }

  /* ------------------------------------------------------------------- DOM */

  // The app's inputs are controlled: a plain `value =` is reverted on the next
  // render. The native setter plus an 'input' event updates the app's state.
  function setControlledValue(input, value) {
    var desc = Object.getOwnPropertyDescriptor(Object.getPrototypeOf(input), 'value');
    if (desc && desc.set) desc.set.call(input, value);
    else input.value = value;
    input.dispatchEvent(new Event('input', { bubbles: true, composed: true }));
    input.dispatchEvent(new Event('change', { bubbles: true, composed: true }));
  }

  var STYLE = [
    '[data-about-hidden="true"] { display: none !important; }',
    /* The consent checkbox sits in its own fieldset, whose legend ("Confirmation")
       only repeats what the checkbox label already says. It is hidden visually
       rather than removed, so the fieldset keeps its accessible group name.
       Keyed on the field name, not the app's build-hashed class names. */
    'fieldset[name="custom#confirmation"] > legend {',
    '  position: absolute !important; width: 1px; height: 1px; margin: -1px;',
    '  padding: 0; overflow: hidden; clip: rect(0 0 0 0); white-space: nowrap; border: 0;',
    '}',
    '.about-ocr-status {',
    '  display: block; margin: 8px 0 0; font-size: 14px; line-height: 1.4;',
    '  text-align: left; color: #595959;',
    '}',
    '.about-ocr-status[data-state="found"] { color: #108043; }',
    '.about-ocr-status[data-state="error"] { color: #e30000; }',
    '.about-ocr-status:empty { display: none; }',

    '.about-ocr-links {',
    '  display: flex; flex-wrap: wrap; gap: 6px 20px; margin: 8px 0 0; text-align: left;',
    '}',
    '.about-ocr-terms { color: #0071e3; text-decoration: underline; }',
    '.about-ocr-links button {',
    '  appearance: none; background: none; border: 0; padding: 0;',
    '  font: inherit; font-size: 14px; line-height: 1.4; color: #0071e3;',
    '  text-decoration: underline; cursor: pointer;',
    '}',

    /* The viewer is appended inside the shadow root, so the app's styles cannot
       reach it and these rules cannot leak out to the rest of the page. */
    '.about-ocr-viewer {',
    '  position: fixed; inset: 0; z-index: 2147483000;',
    '  display: flex; align-items: center; justify-content: center;',
    '  padding: 24px; background: rgba(0, 0, 0, 0.78);',
    '}',
    '.about-ocr-viewer img {',
    '  max-width: 720px; max-height: calc(100vh - 96px);',
    '  width: auto; height: auto; object-fit: contain;',
    '  border-radius: 8px; background: #fff;',
    '}',
    '@media (max-width: 767px) {',
    '  .about-ocr-viewer { padding: 12px; }',
    '  .about-ocr-viewer img { max-width: 100%; max-height: calc(100vh - 72px); }',
    '}',
    '.about-ocr-viewer button {',
    '  position: absolute; top: 16px; right: 16px; width: 40px; height: 40px;',
    '  display: flex; align-items: center; justify-content: center;',
    '  font: inherit; font-size: 24px; line-height: 1; color: #000;',
    '  background: #fff; border: 0; border-radius: 50%; cursor: pointer;',
    '}'
  ].join('\n');

  // A minimal viewer: the client asked only for a link that shows the image.
  function openViewer(shadow, src, label) {
    var existing = shadow.querySelector('.about-ocr-viewer');
    if (existing) existing.remove();

    var overlay = document.createElement('div');
    overlay.className = 'about-ocr-viewer';
    overlay.setAttribute('role', 'dialog');
    overlay.setAttribute('aria-modal', 'true');
    overlay.setAttribute('aria-label', label);

    var img = document.createElement('img');
    img.src = src;
    img.alt = label;

    var close = document.createElement('button');
    close.type = 'button';               // never a submit, so the guard ignores it
    close.textContent = '×';
    close.setAttribute('aria-label', TEXT.close);

    function dismiss() {
      overlay.remove();
      document.removeEventListener('keydown', onKey, true);
    }
    function onKey(e) { if (e.key === 'Escape' || e.keyCode === 27) dismiss(); }

    close.addEventListener('click', dismiss);
    overlay.addEventListener('click', function (e) { if (e.target === overlay) dismiss(); });
    document.addEventListener('keydown', onKey, true);

    overlay.appendChild(img);
    overlay.appendChild(close);
    shadow.appendChild(overlay);
    close.focus();
  }

  function enhance(shadow) {
    if (shadow.__aboutOcrReady) return true;

    var serialEl = shadow.getElementById(SERIAL_FIELD);
    var warrantyEl = shadow.getElementById(WARRANTY_FIELD);
    var fileEl = shadow.getElementById(FILE_FIELD);
    if (!serialEl || !warrantyEl || !fileEl) return false;
    shadow.__aboutOcrReady = true;

    try {
      var sheet = new CSSStyleSheet();
      sheet.replaceSync(STYLE);
      shadow.adoptedStyleSheets = shadow.adoptedStyleSheets.concat([sheet]);
    } catch (e) {
      var tag = document.createElement('style');
      tag.textContent = STYLE;
      shadow.appendChild(tag);
    }

    // 'idle' | 'reading' | 'found' | 'failed'. Only 'found' - both values read
    // from the current screenshot - opens the submit.
    var readState = 'idle';
    var reading = 0;          // sequence guard, so a slow read cannot overwrite a newer one
    var lastState = 'idle', lastMessage = TEXT.hint;

    // Turns the "Terms and Conditions" phrase inside the consent checkbox
    // label into a link. The app owns that label and re-renders it, so this is
    // re-applied from the observer and is a no-op once the link is in place.
    function linkifyTerms() {
      if (!TERMS.url || !TERMS.text) return;
      if (shadow.querySelector('.about-ocr-terms')) return;

      var cb = shadow.querySelector('input[type="checkbox"][id^="custom#confirmation"]');
      if (!cb) return;

      // Matched by property rather than a [for=...] selector: the id contains
      // spaces and a '#', which would have to be escaped.
      var label = null, labels = shadow.querySelectorAll('label');
      for (var i = 0; i < labels.length; i++) {
        if (labels[i].htmlFor === cb.id) { label = labels[i]; break; }
      }
      if (!label) return;

      var walker = document.createTreeWalker(label, NodeFilter.SHOW_TEXT, null);
      var node, at = -1, target = null;
      while ((node = walker.nextNode())) {
        at = node.textContent.indexOf(TERMS.text);
        if (at !== -1) { target = node; break; }
      }
      if (!target) return;

      var phrase = target.splitText(at);
      phrase.splitText(TERMS.text.length);      // leaves `phrase` as just the phrase

      var a = document.createElement('a');
      a.className = 'about-ocr-terms';
      a.href = TERMS.url;
      a.target = '_blank';
      a.rel = 'noopener';
      a.textContent = TERMS.text;
      // The link sits inside the <label>, so without this a click would follow
      // the link and toggle the checkbox at the same time.
      a.addEventListener('click', function (e) { e.stopPropagation(); });
      phrase.parentNode.replaceChild(a, phrase);
    }

    // The two help links. Re-attached from the observer like the status line,
    // because the app replaces this part of its DOM on re-render. A link is
    // only rendered when Liquid actually resolved its file, so a missing or
    // renamed upload degrades to no link rather than a broken one.
    function linksEl() {
      var el = shadow.querySelector('.about-ocr-links');
      if (el && el.isConnected) return el;

      var pairs = [[IMAGES.howTo, TEXT.linkHowTo], [IMAGES.example, TEXT.linkExample]]
        .filter(function (p) { return p[0] && p[0].indexOf('/') !== -1; });
      if (!pairs.length) return null;

      var host = shadow.getElementById(FILE_FIELD);
      host = host && host.closest('[class*="_formFileInput_"]');
      if (!host) return null;

      el = document.createElement('div');
      el.className = 'about-ocr-links';
      pairs.forEach(function (p) {
        var b = document.createElement('button');
        b.type = 'button';
        b.textContent = p[1];
        b.addEventListener('click', function () { openViewer(shadow, p[0], p[1]); });
        el.appendChild(b);
      });
      host.appendChild(el);
      return el;
    }

    function hideFields() {
      [SERIAL_FIELD, WARRANTY_FIELD].forEach(function (id) {
        var el = shadow.getElementById(id);
        var box = el && el.closest('[class*="_formFieldContainer_"]');
        if (box) box.setAttribute('data-about-hidden', 'true');
      });
    }

    // The status line lives in the app's DOM, which the app may replace, so it
    // is re-attached from the observer rather than created once.
    function statusEl() {
      var el = shadow.querySelector('.about-ocr-status');
      if (el && el.isConnected) return el;
      var host = shadow.getElementById(FILE_FIELD);
      host = host && host.closest('[class*="_formFileInput_"]');
      if (!host) return null;
      el = document.createElement('div');
      el.className = 'about-ocr-status';
      el.setAttribute('aria-live', 'polite');
      host.appendChild(el);
      return el;
    }

    function say(state, message) {
      lastState = state;
      lastMessage = message;
      var el = statusEl();
      if (!el) return;
      el.setAttribute('data-state', state);
      el.textContent = message;
    }

    function setField(id, value) {
      var el = shadow.getElementById(id);
      if (el && el.value !== value) setControlledValue(el, value);
    }

    // Any value already in the fields belongs to the previous screenshot.
    function resetFields() {
      readState = 'idle';
      reading++;
      setField(SERIAL_FIELD, '');
      setField(WARRANTY_FIELD, '');
      say('idle', TEXT.hint);
    }

    hideFields();
    linksEl();
    linkifyTerms();
    say('idle', TEXT.hint);

    // The model is large, so start fetching it the moment the customer reaches
    // for the file control rather than after they have chosen a file.
    function prefetch() { getWorker()['catch'](function () {}); }
    shadow.addEventListener('pointerdown', function (ev) {
      var el = ev.target;
      if (el && el.closest && el.closest('[class*="_formFileInput_"]')) prefetch();
    }, true);

    shadow.addEventListener('change', function (ev) {
      var target = ev.target;
      if (!target || target.id !== FILE_FIELD) return;

      var f = target.files && target.files[0];
      resetFields();
      if (!f) return;
      if (f.type.indexOf('image/') !== 0) return;      // the app rejects it anyway
      if (f.size > MAX_BYTES) { readState = 'failed'; say('error', TEXT.tooBig); return; }

      var token = ++reading;
      readState = 'reading';
      say('reading', TEXT.reading);

      readAbout(f).then(function (got) {
        if (token !== reading) return;                 // a newer screenshot won
        if (got.serial && got.warranty) {
          readState = 'found';
          setField(SERIAL_FIELD, got.serial);
          setField(WARRANTY_FIELD, got.warranty);
          hideFields();
          say('found', TEXT.found.replace('{serial}', got.serial).replace('{warranty}', got.warranty));
          return;
        }
        readState = 'failed';
        setField(SERIAL_FIELD, '');
        setField(WARRANTY_FIELD, '');
        if (got.serial || got.warranty) {
          var missing = got.serial ? TEXT.labelWarranty : TEXT.labelSerial;
          say('error', TEXT.partial.replace('{missing}', missing));
        } else {
          say('error', TEXT.notFound);
        }
      })['catch'](function (err) {
        if (token !== reading) return;
        if (window.console && console.warn) console.warn('[about-ocr]', err);
        readState = 'failed';
        setField(SERIAL_FIELD, '');
        setField(WARRANTY_FIELD, '');
        say('error', TEXT.failed);
      });
    }, true);

    function ancestorMatches(node, test) {
      for (var el = node; el && el !== shadow; el = el.parentNode) {
        if (el.nodeType === 1 && test(el)) return true;
      }
      return false;
    }

    function isClearButton(node) {
      return ancestorMatches(node, function (el) { return el.id === CLEAR_BUTTON; });
    }

    function isSubmit(node) {
      return ancestorMatches(node, function (el) { return el.getAttribute('type') === 'submit'; });
    }

    // The only route to a submit is a screenshot both values were read from.
    // Blocking in the capture phase at the shadow root stops the event before
    // the app's own handler on the button or the form ever sees it.
    function blockUnlessRead(ev) {
      if (readState === 'found') return;
      ev.preventDefault();
      ev.stopPropagation();
      if (ev.stopImmediatePropagation) ev.stopImmediatePropagation();

      if (readState === 'reading') say('reading', TEXT.stillReading);
      else if (readState === 'failed') say('error', lastMessage === TEXT.hint ? TEXT.blocked : lastMessage);
      else say('error', TEXT.blocked);

      var box = shadow.getElementById(FILE_FIELD);
      box = box && box.closest('[class*="_formFileInput_"]');
      if (box && box.scrollIntoView) box.scrollIntoView({ block: 'center', behavior: 'smooth' });
    }

    shadow.addEventListener('click', function (ev) {
      if (isClearButton(ev.target)) { resetFields(); return; }
      if (isSubmit(ev.target)) blockUnlessRead(ev);
    }, true);

    // Enter in a text field submits without any click.
    shadow.addEventListener('submit', blockUnlessRead, true);

    // The app re-renders on validation and on every keystroke, dropping the
    // marker attributes and the status line. Both are re-applied here. The
    // fields are never revealed: typing them by hand is what this prevents.
    new MutationObserver(function () {
      hideFields();
      linksEl();
      linkifyTerms();
      var el = statusEl();
      if (el && el.textContent !== lastMessage) {
        el.setAttribute('data-state', lastState);
        el.textContent = lastMessage;
      }
      var f = shadow.getElementById(FILE_FIELD);
      if (f && f.files && f.files.length === 0 && readState !== 'idle' && readState !== 'reading') {
        resetFields();
      }
    }).observe(shadow, {
      childList: true, subtree: true, attributes: true, attributeFilter: ['aria-invalid']
    });

    return true;
  }

  function boot() {
    var tries = 0;
    var timer = setInterval(function () {
      var host = document.querySelector('[data-form-root] #app-embed');
      var shadow = host && host.shadowRoot;
      if (shadow && enhance(shadow)) return clearInterval(timer);
      if (++tries > 120) clearInterval(timer);   // ~60s, then give up
    }, 500);
  }

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', boot);
  else boot();
})();
